import React from 'react';
import BaseModal from '../BaseModal/BaseModal';
import PaymentForm from '../PaymentForm/PaymentForm';
import PendingDialog from '../PendingDialog/PendingDialog';
import SuccessDialog from '../SuccessDialog/SuccessDialog';
import FailedDialog from '../FailedDialog/FailedDialog';
import ExpiredDialog from '../ExpiredDialog/ExpiredDialog';
import LoadingDialog from '../LoadingDialog/LoadingDialog';
import UnavailableDialog from '../UnavailableDialog/UnavailableDialog';
import { usePayment } from '../../hooks/usePayment';
import { useLanguage } from '../../context/LanguageContext';

interface PaymentModalProps {
  paymentId: string;
}

const PaymentModal: React.FC<PaymentModalProps> = ({ paymentId }) => {
  const { t } = useLanguage();
  const {
    paymentData,
    loading,
    error,
    confirming,
    timeRemaining,
    confirmPayment,
    reFetchPayment
  } = usePayment(paymentId);
  const [refreshing, setRefreshing] = React.useState(false);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}${t('minutesShort')}:${secs.toString().padStart(2, '0')}${t('secondsShort')}`;
  };

  const redirectUrl = paymentData?.details?.redirect_url;
  const cancelRedirectUrl = paymentData?.details?.cancel_redirect_url;

  const handleFormSubmit = async () => {
    await confirmPayment();
  };

  const handleCancel = () => {
    if (cancelRedirectUrl) {
      window.location.href = cancelRedirectUrl;
    }
  };

  const handleTryAgain = () => {
    if (cancelRedirectUrl) {
      window.location.href = cancelRedirectUrl;
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await reFetchPayment();
    setRefreshing(false);
  };

  const backToSite = () => {
    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  };

  if (loading && !paymentData) {
    return (
      <div className="grid-item">
        <LoadingDialog />
      </div>
    );
  }

  if (error && !paymentData) {
    return (
      <div className="grid-item">
        <UnavailableDialog />
      </div>
    );
  }

  if (!paymentData) {
    return (
      <div className="grid-item">
        <UnavailableDialog />
      </div>
    );
  }

  const isExpired = paymentData.status === 'expired';
  const isFailed = paymentData.status === 'failed' || paymentData.status === 'dispute';
  const isSuccess = paymentData.status === 'success';
  const isConfirmed = paymentData.status === 'confirmed';

  if (isSuccess) {
    return (
      <div className="grid-item">
        <SuccessDialog
          onBackToMerchant={redirectUrl ? backToSite : undefined}
        />
      </div>
    );
  }

  if (isFailed) {
    return (
      <div className="grid-item">
        <FailedDialog
          onTryAgain={cancelRedirectUrl ? handleTryAgain : undefined}
        />
      </div>
    );
  }

  if (isExpired) {
    return (
      <div className="grid-item">
        <ExpiredDialog
          onTryAgain={cancelRedirectUrl ? handleTryAgain : undefined}
        />
      </div>
    );
  }

  if (isConfirmed) {
    return (
      <div className="grid-item">
        <PendingDialog
          paymentId={paymentId}
          timeRemaining={timeRemaining}
          onRefresh={handleRefresh}
          onBackToMerchant={redirectUrl ? backToSite : undefined}
          refreshing={refreshing}
          supportsReceipt={paymentData.supports_receipt}
        />
      </div>
    );
  }

  const getBadgeVariant = (): 'danger' | 'success' | 'info' => {
    if (timeRemaining <= 60) return 'danger';
    return 'info';
  };

  const badge = {
    variant: getBadgeVariant(),
    time: formatTime(timeRemaining)
  };

  return (
    <BaseModal
      amount={String(paymentData.amount)}
      currency={paymentData.currency}
      showCloseButton={true}
    >
      <PaymentForm
        initialData={{
          cardNumber: paymentData.card?.number || '',
          accountHolder: paymentData.card?.recipient_name || '',
          bankName: paymentData.card?.bank_name || ''
        }}
        onSubmit={handleFormSubmit}
        onCancel={cancelRedirectUrl ? handleCancel : undefined}
        confirming={confirming}
        error={error}
        paymentStatus={paymentData.status}
        timeExpired={timeRemaining <= 0}
        badge={badge}
      />
    </BaseModal>
  );
};

export default PaymentModal;
